import React, { useState } from "react";
import { Box, Typography, IconButton, Tabs, Tab, Button, Chip, CircularProgress, Tooltip, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WarningAmberIcon from "@material-ui/icons/Warning";
import WifiIcon from "@material-ui/icons/Wifi";
import WifiOffIcon from "@material-ui/icons/WifiOff";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import TimerIcon from "@material-ui/icons/Timer";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RefreshIcon from "@material-ui/icons/Refresh";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { TabPanel } from "../../../../../../components/TabPannel/TabPannel";
import { dtcBadge, INDICATOR_BADGES, INDICATOR_SEVERITY_LABEL, INDICATOR_SEVERITY_ORDER } from "../severity";
import { useVehicleDtc } from "../useVehicleDtc";
import { useVehicleIndicators } from "../useVehicleIndicators";
import { useVehicleCrash } from "../useVehicleCrash";
import { PeriodStats } from "./useVehicleTracking";
import { Vehicle, IndicatorSeverity } from "../types";

const TABS = ["Overview", "DTC", "Indicators", "Crash Data"];

// Converted from nuvven-b2b-app's sx-based (MUI v5) design to v4's makeStyles — see
// TrackingHistoryBar.tsx for why literal pixels are used instead of theme.spacing().
const useStyles = makeStyles((theme) => ({
  emptyState: { display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 24px", gap: 8, color: "#9ca3af", textAlign: "center" },
  emptyMessage: { fontSize: 13, color: "#6b7280" },
  emptySub: { fontSize: 11 },

  tabHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px 4px 16px" },
  tabHeaderTitle: { fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 },

  overviewRoot: { padding: 16, display: "flex", flexDirection: "column", gap: 12 },
  summaryBox: { borderRadius: 12, backgroundColor: "color-mix(in srgb, var(--brand-accent) 8%, white)", border: "1px solid color-mix(in srgb, var(--brand-accent) 25%, white)", padding: 12 },
  summaryTitle: { fontSize: 10, fontWeight: 600, color: "color-mix(in srgb, var(--brand-accent) 60%, white)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 },
  summaryGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  // minWidth:0 is the actual fix — CSS Grid items default to min-width:auto, which lets
  // intrinsic content size (e.g. a long VIN) win over the "1fr" track and overflow the grid
  // sideways instead of respecting it, so the ellipsis truncation below never gets a chance
  // to kick in and the whole panel becomes horizontally scrollable.
  summaryCard: { backgroundColor: "#fff", borderRadius: 8, padding: "8px 10px", border: "1px solid color-mix(in srgb, var(--brand-accent) 25%, white)", minWidth: 0, overflow: "hidden" },
  summaryLabel: { fontSize: 10, color: "#9ca3af" },
  summaryValue: { fontWeight: 700, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  summaryUnavailable: { fontSize: 11, color: "#6b7280" },

  detailsSectionTitle: { fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 },
  detailsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  detailCard: { backgroundColor: "#f9fafb", borderRadius: 8, border: "1px solid #f3f4f6", padding: "8px 10px", minWidth: 0, overflow: "hidden" },
  detailCardWide: { gridColumn: "1 / -1" },
  detailLabel: { fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 },
  detailValue: { fontWeight: 700, fontSize: 11.5, color: "#1f2937", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  detailValueWide: { whiteSpace: "normal" },
  fuelBarTrack: { marginTop: 4, height: 4, width: "100%", borderRadius: 12, backgroundColor: "#e5e7eb", overflow: "hidden" },
  fuelBarFill: { height: "100%", borderRadius: 12 },

  loadingCenter: { display: "flex", justifyContent: "center", padding: "32px 0" },
  indicatorGroups: { padding: "0 16px 16px 16px", display: "flex", flexDirection: "column", gap: 16 },
  indicatorGroupTitle: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 },
  indicatorItem: { display: "flex", alignItems: "center", gap: 12, borderRadius: 12, padding: "8px 12px" },
  indicatorIcon: { fontSize: 14, flexShrink: 0 },
  indicatorLabel: { fontSize: 12, color: "#4b5563" },

  crashList: { padding: "0 16px 16px 16px", display: "flex", flexDirection: "column", gap: 8 },
  crashCard: { borderRadius: 12, border: "1px solid #fecaca", backgroundColor: "#fef2f2", padding: 12 },
  crashBadgeRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 },
  severityChip: { height: 18, fontSize: 10, backgroundColor: "#fee2e2", color: "#b91c1c" },
  directionChip: { height: 18, fontSize: 10, backgroundColor: "rgba(255,255,255,0.6)", color: "#6b7280" },
  crashAddress: { fontSize: 12, color: "#7f1d1d", opacity: 0.9 },
  crashDateRow: { display: "flex", alignItems: "center", gap: 4, marginTop: 6, opacity: 0.6 },
  crashDateIcon: { fontSize: 12, color: "#b91c1c" },
  crashDateText: { fontSize: 10, color: "#b91c1c" },

  dtcList: { padding: "0 16px 16px 16px", display: "flex", flexDirection: "column", gap: 8 },
  dtcCard: { borderRadius: 12, padding: 12 },
  dtcTopRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 },
  dtcCode: { fontFamily: "monospace", fontWeight: 700, fontSize: 13 },
  dtcSystemChip: { height: 18, fontSize: 10, backgroundColor: "rgba(255,255,255,0.6)", color: "#6b7280" },
  dtcDescription: { fontSize: 12, opacity: 0.9 },
  dtcDateRow: { display: "flex", alignItems: "center", gap: 4, marginTop: 6, opacity: 0.6 },
  dtcDateIcon: { fontSize: 12 },
  dtcDateText: { fontSize: 10 },

  panelRoot: {
    position: "absolute",
    top: 16,
    right: 16,
    zIndex: 1000,
    width: 320,
    maxHeight: "calc(100% - 32px)",
    backgroundColor: "#fff",
    borderRadius: 16,
    boxShadow: theme.shadows[8],
    border: "1px solid #f3f4f6",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  headerBox: { padding: "16px 16px 0 16px", backgroundColor: "var(--brand-accent)", flexShrink: 0 },
  headerTopRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 },
  headerTitleRow: { display: "flex", alignItems: "center", gap: 8 },
  headerPlate: { fontWeight: 700, color: "#fff", fontSize: 15 },
  headerSubtitle: { color: "rgba(255,255,255,0.72)", fontSize: 12, marginTop: 2 },
  driverRow: { display: "flex", alignItems: "center", gap: 4, marginTop: 4 },
  driverIcon: { fontSize: 14, color: "rgba(255,255,255,0.72)" },
  driverName: { color: "rgba(255,255,255,0.72)", fontSize: 12 },
  closeBtn: { color: "rgba(255,255,255,0.6)", "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.1)" } },
  statusRow: { display: "flex", alignItems: "center", gap: 10, paddingBottom: 12 },
  statusPill: { display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600 },
  statusDivider: { width: 1, height: 12, backgroundColor: "rgba(255,255,255,0.2)" },
  drivingBadge: { width: 16, height: 16, borderRadius: 6, backgroundColor: "#f97316", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },

  tabsBox: { backgroundColor: "var(--brand-accent)", flexShrink: 0 },
  tabIndicator: { backgroundColor: "var(--primary-color)", height: 3, borderRadius: 1.5 },
  tabsBody: { flex: 1, overflowY: "auto" },
}));

// Shared "nothing to show" block for every real-data detail tab.
function EmptyState({
  Icon = InfoOutlinedIcon,
  iconColor = "#d1d5db",
  message,
  sub,
}: {
  Icon?: typeof InfoOutlinedIcon;
  iconColor?: string;
  message: string;
  sub?: string;
}) {
  const classes = useStyles();
  return (
    <Box className={classes.emptyState}>
      <Icon style={{ fontSize: 32, color: iconColor }} />
      <Typography className={classes.emptyMessage}>{message}</Typography>
      {sub && <Typography className={classes.emptySub}>{sub}</Typography>}
    </Box>
  );
}

// Title + refresh button shown at the top of every real-data detail tab, so any tab's data
// can be pulled again on demand instead of waiting for the next cache-and-network refresh.
function DetailTabHeader({ title, onRefresh, refreshing }: { title: string; onRefresh: () => void; refreshing?: boolean }) {
  const classes = useStyles();
  return (
    <Box className={classes.tabHeader}>
      <Typography className={classes.tabHeaderTitle}>{title}</Typography>
      <Tooltip title="Refresh">
        <span>
          <IconButton size="small" onClick={() => onRefresh()} disabled={refreshing}>
            {refreshing ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function OverviewTab({ vehicle, trackingHours, periodStats }: { vehicle: Vehicle; trackingHours: number | null; periodStats?: PeriodStats }) {
  const classes = useStyles();
  // periodStats comes from the on-select /fleetDashboard/telematics fetch (fresher, per-vehicle
  // CAN/GPS reading) — vehicle.location/vehicle.fuel are the Couchbase-cached, list-level values
  // and are only a fallback for while that fetch hasn't resolved yet or has nothing to report.
  const fuelPct = periodStats?.latestFuelLevel ?? vehicle.fuel;
  const fuelColor = fuelPct == null ? "#9ca3af" : fuelPct < 20 ? "#ef4444" : fuelPct < 50 ? "#f59e0b" : "#22c55e";

  const rows = [
    { label: "Location", value: periodStats?.address ?? vehicle.location, wide: true },
    { label: "Fuel Level", value: fuelPct != null ? `${fuelPct}%` : "Not available", extra: "fuel" },
    { label: "VIN", value: vehicle.vin || "—" },
    { label: "Telematics ID", value: vehicle.telemetryDeviceId || "—" },
    { label: "Last Odometer", value: periodStats?.latestOdometer ?? vehicle.odometer },
    { label: "Fuel Type", value: vehicle.fuelType || "—" },
  ];

  return (
    <Box className={classes.overviewRoot}>
      {trackingHours && (
        <Box className={classes.summaryBox}>
          <Typography className={classes.summaryTitle}>Last {trackingHours}h Summary</Typography>
          {periodStats ? (
            <Box className={classes.summaryGrid}>
              {[
                { label: "Distance", value: periodStats.distance, color: "var(--brand-accent)" },
                { label: "Engine Hours", value: periodStats.engineHours, color: "#059669" },
                { label: "Fuel Usage", value: periodStats.fuelUsage, color: "#d97706" },
                { label: "Stoppage", value: periodStats.stoppageHours, color: "#7c3aed" },
              ].map((s) => (
                <Box key={s.label} className={classes.summaryCard}>
                  <Typography className={classes.summaryLabel}>{s.label}</Typography>
                  <Typography className={classes.summaryValue} style={{ color: s.color }}>{s.value}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography className={classes.summaryUnavailable}>Not available for this vehicle's device type.</Typography>
          )}
        </Box>
      )}

      <Box>
        <Typography className={classes.detailsSectionTitle}>Vehicle Details</Typography>
        <Box className={classes.detailsGrid}>
          {rows.map((row) => (
            <Box key={row.label} className={`${classes.detailCard} ${row.wide ? classes.detailCardWide : ""}`}>
              <Typography className={classes.detailLabel}>{row.label}</Typography>
              <Typography className={`${classes.detailValue} ${row.wide ? classes.detailValueWide : ""}`}>
                {row.value}
              </Typography>
              {row.extra === "fuel" && fuelPct != null && (
                <Box className={classes.fuelBarTrack}>
                  <Box className={classes.fuelBarFill} style={{ width: `${fuelPct}%`, backgroundColor: fuelColor }} />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// Decouples the header refresh button's own spinner from the tab body's initial-load spinner —
// without this, a manual refresh click (which re-flips the query's `loading` flag) made both
// the button AND the body flash a loader even though the body already had data to keep showing.
function useManualRefresh(refetch: () => Promise<unknown>) {
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };
  return { refreshing, handleRefresh };
}

function IndicatorsDetailTab({ vehicle }: { vehicle: Vehicle }) {
  const classes = useStyles();
  const { indicatorsByEsn, loading, refetch } = useVehicleIndicators(vehicle.telemetryDeviceId ? [vehicle.telemetryDeviceId] : []);
  const { refreshing, handleRefresh } = useManualRefresh(refetch);
  const active = vehicle.telemetryDeviceId ? indicatorsByEsn[vehicle.telemetryDeviceId] ?? [] : [];

  if (!vehicle.telemetryDeviceId) {
    return <EmptyState message="Indicator data isn't available for this device type" />;
  }

  const bySeverity = INDICATOR_SEVERITY_ORDER.map((severity: IndicatorSeverity) => ({
    severity,
    items: active.filter((ind) => ind.severity === severity),
  })).filter((group) => group.items.length > 0);

  return (
    <Box>
      <DetailTabHeader title="Dashboard Indicators" onRefresh={handleRefresh} refreshing={refreshing} />
      {loading && !refreshing && active.length === 0 ? (
        <Box className={classes.loadingCenter}>
          <CircularProgress size={20} />
        </Box>
      ) : active.length === 0 ? (
        <EmptyState Icon={CheckCircleIcon} iconColor="#86efac" message="No active dashboard warnings in the last 24 hours" />
      ) : (
        <Box className={classes.indicatorGroups}>
          {bySeverity.map(({ severity, items }) => (
            <Box key={severity}>
              <Typography className={classes.indicatorGroupTitle} style={{ color: INDICATOR_BADGES[severity].color, marginBottom: 8 }}>
                {INDICATOR_SEVERITY_LABEL[severity]} ({items.length})
              </Typography>
              <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {items.map((ind, i) => (
                  <Box
                    key={`${ind.field}-${i}`}
                    className={classes.indicatorItem}
                    style={{ border: `1px solid ${INDICATOR_BADGES[severity].border}`, backgroundColor: INDICATOR_BADGES[severity].bg }}
                  >
                    <WarningAmberIcon className={classes.indicatorIcon} style={{ color: INDICATOR_BADGES[severity].color }} />
                    <Typography className={classes.indicatorLabel}>{ind.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function CrashDetailTab({ vehicle }: { vehicle: Vehicle }) {
  const classes = useStyles();
  const { crashesByEsn, loading, refetch } = useVehicleCrash(vehicle.telemetryDeviceId ? [vehicle.telemetryDeviceId] : []);
  const { refreshing, handleRefresh } = useManualRefresh(refetch);
  const crashes = vehicle.telemetryDeviceId ? crashesByEsn[vehicle.telemetryDeviceId] ?? [] : [];

  if (!vehicle.telemetryDeviceId) {
    return <EmptyState message="Crash data isn't available for this device type" />;
  }

  return (
    <Box>
      <DetailTabHeader title="Crash Events" onRefresh={handleRefresh} refreshing={refreshing} />
      {loading && !refreshing && crashes.length === 0 ? (
        <Box className={classes.loadingCenter}>
          <CircularProgress size={20} />
        </Box>
      ) : crashes.length === 0 ? (
        <EmptyState Icon={CheckCircleIcon} iconColor="#86efac" message="No crash events in the last 24 hours" />
      ) : (
        <Box className={classes.crashList}>
          {crashes.map((c, i) => {
            const address = [c.addressLine1, c.addressLine2, c.city, c.state, c.country].filter(Boolean).join(", ");
            return (
              <Box key={`${c.crashId}-${i}`} className={classes.crashCard}>
                <Box className={classes.crashBadgeRow}>
                  {c.severity && <Chip label={c.severity} size="small" className={classes.severityChip} />}
                  {c.crashDirection && <Chip label={c.crashDirection} size="small" className={classes.directionChip} />}
                </Box>
                {address && <Typography className={classes.crashAddress}>{address}</Typography>}
                <Box className={classes.crashDateRow}>
                  <TimerIcon className={classes.crashDateIcon} />
                  <Typography className={classes.crashDateText}>{c.datetime}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function DTCDetailTab({ vehicle }: { vehicle: Vehicle }) {
  const classes = useStyles();
  const { dtcByEsn, loading, refetch } = useVehicleDtc(vehicle.telemetryDeviceId ? [vehicle.telemetryDeviceId] : []);
  const { refreshing, handleRefresh } = useManualRefresh(refetch);
  const dtcs = vehicle.telemetryDeviceId ? dtcByEsn[vehicle.telemetryDeviceId] ?? [] : [];

  if (!vehicle.telemetryDeviceId) {
    return <EmptyState message="DTC data isn't available for this device type" />;
  }

  return (
    <Box>
      <DetailTabHeader title="DTC Alerts" onRefresh={handleRefresh} refreshing={refreshing} />
      {loading && !refreshing && dtcs.length === 0 ? (
        <Box className={classes.loadingCenter}>
          <CircularProgress size={20} />
        </Box>
      ) : dtcs.length === 0 ? (
        <EmptyState Icon={CheckCircleIcon} iconColor="#86efac" message="No DTC codes in the last 24 hours" />
      ) : (
        <Box className={classes.dtcList}>
          {dtcs.map((d, i) => (
            <Box key={`${d.code}-${i}`} className={classes.dtcCard} style={{ border: `1px solid ${dtcBadge.border}`, backgroundColor: dtcBadge.bg }}>
              <Box className={classes.dtcTopRow}>
                <Typography className={classes.dtcCode} style={{ color: dtcBadge.color }}>{d.code}</Typography>
                <Chip label={d.system} size="small" className={classes.dtcSystemChip} />
              </Box>
              <Typography className={classes.dtcDescription} style={{ color: dtcBadge.color }}>{d.description}</Typography>
              <Box className={classes.dtcDateRow}>
                <TimerIcon className={classes.dtcDateIcon} style={{ color: dtcBadge.color }} />
                <Typography className={classes.dtcDateText} style={{ color: dtcBadge.color }}>{d.detectedAt}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

interface VehicleDetailPanelProps {
  vehicle: Vehicle;
  onClose: () => void;
  trackingHours: number | null;
  periodStats?: PeriodStats;
}

export function VehicleDetailPanel({ vehicle, onClose, trackingHours, periodStats }: VehicleDetailPanelProps) {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  // speedVal is the last cached telematicCoreData.speed and isn't reset when the device drops
  // connection — without the status check, an offline vehicle can show a stale speed and
  // "Driving" state forever.
  const isDriving = vehicle.status === "online" && vehicle.speedVal > 0;

  return (
    <Box className={classes.panelRoot}>
      <Box className={classes.headerBox}>
        <Box className={classes.headerTopRow}>
          <Box>
            <Box className={classes.headerTitleRow}>
              <Typography className={classes.headerPlate}>{vehicle.plate}</Typography>
            </Box>
            <Typography className={classes.headerSubtitle}>{vehicle.make} {vehicle.model}{vehicle.type ? ` · ${vehicle.type}` : ""}</Typography>
            {vehicle.driverName && (
              <Box className={classes.driverRow}>
                <PersonOutlineIcon className={classes.driverIcon} />
                <Typography className={classes.driverName}>{vehicle.driverName}</Typography>
              </Box>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} className={classes.closeBtn}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className={classes.statusRow}>
          <Box className={classes.statusPill} style={{ color: vehicle.status === "online" ? "#86efac" : "#94a3b8" }}>
            {vehicle.status === "online" ? <WifiIcon style={{ fontSize: 15 }} /> : <WifiOffIcon style={{ fontSize: 15 }} />}
            {vehicle.status === "online" ? "Online" : "Offline"}
          </Box>
          <Box className={classes.statusDivider} />
          <Box className={classes.statusPill} style={{ color: isDriving ? "#fb923c" : "#94a3b8" }}>
            {isDriving ? (
              <Box className={classes.drivingBadge}>
                <PlayArrowIcon style={{ fontSize: 12, color: "#fff" }} />
              </Box>
            ) : (
              <PauseIcon style={{ fontSize: 15 }} />
            )}
            {isDriving ? "Driving" : "Parked"}
          </Box>
        </Box>
      </Box>

      {/* fullWidth divides the 4 tabs evenly across the available width, guaranteeing a single
          line with no scroll buttons — small font/padding so all 4 labels fit without wrapping.
          Colors set via inline `style` (not just the makeStyles `classes` overrides) — v4's own
          default Tab/Tabs styles were winning over the JSS class overrides here, same "tabs
          render invisible/white" issue hit earlier on the v5 port; inline style has unconditional
          priority regardless of JSS insertion order, so it can't lose that fight. */}
      <Box className={classes.tabsBox} style={{ backgroundColor: "var(--brand-accent)" }}>
        <Tabs
          value={tab}
          onChange={(_, val) => setTab(val)}
          variant="fullWidth"
          classes={{ indicator: classes.tabIndicator }}
          style={{ minHeight: 34, backgroundColor: "var(--brand-accent)" }}
        >
          {TABS.map((t, i) => (
            <Tab
              key={t}
              label={t}
              style={{
                minHeight: 34,
                minWidth: 0,
                fontSize: 11,
                textTransform: "none",
                padding: "0 4px",
                whiteSpace: "nowrap",
                color: tab === i ? "#fff" : "rgba(255,255,255,0.7)",
                fontWeight: tab === i ? 700 : 500,
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box className={classes.tabsBody}>
        <TabPanel value={tab} index={0}>
          <OverviewTab vehicle={vehicle} trackingHours={trackingHours} periodStats={periodStats} />
        </TabPanel>
        <TabPanel value={tab} index={1}>
          <DTCDetailTab vehicle={vehicle} />
        </TabPanel>
        <TabPanel value={tab} index={2}>
          <IndicatorsDetailTab vehicle={vehicle} />
        </TabPanel>
        <TabPanel value={tab} index={3}>
          <CrashDetailTab vehicle={vehicle} />
        </TabPanel>
      </Box>
    </Box>
  );
}
